/**
 * Admin Dispute Detail (`/admin/disputes/:disputeId`).
 *
 * The arbiter/admin's resolution screen. Loads a single dispute with full
 * context (deal, parties, evidence) in one round-trip and lets the
 * operator pick an outcome + write a note + execute on-chain.
 *
 * Outcome shapes:
 *   buyer_wins    → contract refunds buyer (full)
 *   seller_wins   → contract releases to seller minus platform fee
 *   mutual_cancel → contract refunds buyer (full)
 *   split         → contract pays both, requires explicit nanotons split
 *
 * Per project memory (Business Rule "Dispute default outcome"): when in
 * doubt the money should go back to the buyer. Seller must prove delivery
 * to flip the outcome. We default the outcome dropdown to `buyer_wins`
 * for that reason — the operator has to actively pick another option.
 *
 * Non-custodial: every outcome above maps to an op message the backend
 * sends to the escrow contract. The platform wallet only signs the op.
 *
 * The submit flow has a 2-step confirm dialog because the action is
 * non-reversible — same defensive pattern as the user-facing dispute
 * button (Rule M / Option B applied here too).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DisputeChat } from '@/components/common/DisputeChat';
import { disputes as disputesApi, isApiError } from '@/lib/api';
import type {
  AdminDisputeDetail,
  Evidence,
  ResolveDisputeBody,
} from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { useAppStore } from '@/lib/state/store';
import { nanoToTon, shortAddress, tonToNano } from '@/lib/ton/format';
import { formatDateTime } from '@/lib/utils/format';
import { haptics } from '@/lib/telegram/haptics';
import { cn } from '@/lib/utils/cn';
import { ROUTES } from '@/routes/router';

type Outcome = 'buyer_wins' | 'seller_wins' | 'mutual_cancel' | 'split';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; detail: AdminDisputeDetail }
  | { kind: 'forbidden' }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string };

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'confirming' }
  | { kind: 'submitting' }
  | { kind: 'success'; txHash?: string }
  | { kind: 'error'; message: string };

interface FormState {
  outcome: Outcome;
  note: string;
  buyerAmount: string; // TON string for split
  sellerAmount: string; // TON string for split
}

const INITIAL_FORM: FormState = {
  outcome: 'buyer_wins', // business rule: refund buyer by default
  note: '',
  buyerAmount: '',
  sellerAmount: '',
};

function partyLabel(p: AdminDisputeDetail['buyer']): string {
  if (!p) return '—';
  if (p.username) return `@${p.username}`;
  if (p.firstName) return p.firstName;
  return `tg:${p.telegramId}`;
}

function evidenceSourceLabel(
  ev: Evidence,
  detail: AdminDisputeDetail,
  t: ReturnType<typeof useT>,
): string {
  if (ev.submittedBy === detail.deal.buyerId) {
    return t('admin.detail.evidenceFromBuyer');
  }
  if (ev.submittedBy === detail.deal.sellerId) {
    return t('admin.detail.evidenceFromSeller');
  }
  return t('admin.detail.evidenceFrom');
}

export function AdminDisputeDetailPage(): JSX.Element {
  const { disputeId } = useParams<{ disputeId: string }>();
  const navigate = useNavigate();
  const t = useT();
  const userSnapshot = useAppStore((state) => state.user);

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submit, setSubmit] = useState<SubmitState>({ kind: 'idle' });

  // Frontend role gate (defense in depth — backend enforces too).
  useEffect(() => {
    if (!userSnapshot) return;
    if (userSnapshot.role !== 'admin' && userSnapshot.role !== 'arbiter') {
      navigate(ROUTES.deals, { replace: true });
    }
  }, [userSnapshot, navigate]);

  const load = useCallback(async () => {
    if (!disputeId) {
      setState({ kind: 'not-found' });
      return;
    }
    setState({ kind: 'loading' });
    try {
      const response = await disputesApi.getAdminDispute(disputeId);
      setState({ kind: 'ready', detail: response.dispute });
    } catch (err) {
      if (isApiError(err) && (err.is('unauthorized_actor') || err.status === 401)) {
        setState({ kind: 'forbidden' });
        return;
      }
      if (isApiError(err) && err.is('deal_not_found')) {
        setState({ kind: 'not-found' });
        return;
      }
      const message = err instanceof Error ? err.message : t('admin.queue.loadError');
      setState({ kind: 'error', message });
    }
  }, [disputeId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Pre-fill split amounts to a 50/50 the first time the operator picks
  // the split outcome — they can override afterwards.
  useEffect(() => {
    if (form.outcome !== 'split') return;
    if (form.buyerAmount !== '' || form.sellerAmount !== '') return;
    if (state.kind !== 'ready') return;
    const half = BigInt(state.detail.deal.amount) / 2n;
    const halfStr = nanoToTon(half, 4);
    setForm((prev) => ({ ...prev, buyerAmount: halfStr, sellerAmount: halfStr }));
  }, [form.outcome, form.buyerAmount, form.sellerAmount, state]);

  // ── Validation ──────────────────────────────────────────
  const formError = useMemo(() => {
    if (form.note.trim().length < 4) {
      return t('admin.detail.noteRequired');
    }
    if (form.outcome === 'split') {
      if (state.kind !== 'ready') return null;
      try {
        const buyer = tonToNano(form.buyerAmount);
        const seller = tonToNano(form.sellerAmount);
        const total = BigInt(state.detail.deal.amount);
        if (buyer < 0n || seller < 0n) return t('admin.detail.splitWarn');
        if (buyer + seller > total) return t('admin.detail.splitWarn');
      } catch {
        return t('admin.detail.splitWarn');
      }
    }
    return null;
  }, [form, state, t]);

  const canSubmit =
    state.kind === 'ready' && submit.kind === 'idle' && formError === null;

  // ── Submission ──────────────────────────────────────────
  const handleConfirm = useCallback(() => {
    if (!canSubmit) return;
    haptics.medium();
    setSubmit({ kind: 'confirming' });
  }, [canSubmit]);

  const handleResolve = useCallback(async () => {
    if (state.kind !== 'ready' || !disputeId) return;
    setSubmit({ kind: 'submitting' });
    try {
      const body: ResolveDisputeBody =
        form.outcome === 'split'
          ? {
              outcome: 'split',
              note: form.note.trim(),
              buyerAmount: tonToNano(form.buyerAmount).toString(),
              sellerAmount: tonToNano(form.sellerAmount).toString(),
            }
          : {
              outcome: form.outcome,
              note: form.note.trim(),
            };

      const response = await disputesApi.resolveDispute(disputeId, body);
      haptics.success();
      setSubmit({ kind: 'success', txHash: response.result.txHash });
      // Refresh the detail so the resolved state is rendered.
      void load();
    } catch (err) {
      haptics.error();
      const message = err instanceof Error ? err.message : t('admin.detail.error');
      setSubmit({ kind: 'error', message });
    }
  }, [state, form, disputeId, load, t]);

  // ── Render branches ─────────────────────────────────────
  if (state.kind === 'loading') {
    return (
      <AppShell>
        <PageHeader title={t('admin.detail.title')} />
        <Card className="space-y-3">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/3" />
        </Card>
      </AppShell>
    );
  }

  if (state.kind === 'forbidden') {
    return (
      <AppShell>
        <PageHeader title={t('admin.detail.title')} />
        <Card className="flex items-start gap-3 border-danger/40 bg-danger/10">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden />
          <p className="text-sm text-text-secondary">{t('admin.queue.unauthorized')}</p>
        </Card>
      </AppShell>
    );
  }

  if (state.kind === 'not-found') {
    return (
      <AppShell>
        <PageHeader title={t('admin.detail.title')} />
        <Card className="flex flex-col items-start gap-3">
          <p className="text-sm text-text-secondary">{t('admin.detail.notFound')}</p>
          <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.adminDisputes)}>
            <ArrowLeft className="h-4 w-4" aria-hidden />
            {t('admin.detail.backToQueue')}
          </Button>
        </Card>
      </AppShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <AppShell>
        <PageHeader title={t('admin.detail.title')} />
        <Card className="flex flex-col items-start gap-3 border-danger/40 bg-danger/10">
          <p className="text-sm text-text-secondary">{state.message}</p>
          <Button variant="outline" size="sm" onClick={load}>
            {t('common.retry')}
          </Button>
        </Card>
      </AppShell>
    );
  }

  const { detail } = state;
  const { dispute, deal, buyer, seller, filedBy, evidence } = detail;
  const amountDisplay = nanoToTon(BigInt(deal.amount), 4);
  const isResolved = dispute.status === 'resolved' || dispute.status === 'closed';

  return (
    <AppShell>
      <PageHeader
        title={t('admin.detail.title')}
        description={t('admin.detail.subtitle')}
        action={
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('admin.detail.backToQueue')}
            onClick={() => navigate(ROUTES.adminDisputes)}
          >
            <ArrowLeft className="h-5 w-5" aria-hidden />
          </Button>
        }
      />

      {/* ─── Deal snapshot ───────────────────────────── */}
      <Card className="space-y-2.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-text-muted">
              {t('admin.detail.sectionDeal')}
            </p>
            <p className="mt-1 truncate text-sm font-semibold text-text-primary">
              {deal.title}
            </p>
            <p className="text-xs text-text-muted">#{deal.shortId}</p>
          </div>
          <Badge variant="danger">{dispute.status}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
          <div>
            <span className="text-text-muted">{t('admin.queue.amount')}</span>
            <p className="font-medium text-text-primary">{amountDisplay} TON</p>
          </div>
          <div>
            <span className="text-text-muted">{t('admin.detail.contractAddress')}</span>
            <p className="truncate font-mono text-[11px] text-text-primary">
              {deal.contractAddress ? shortAddress(deal.contractAddress, 6, 6) : '—'}
            </p>
          </div>
        </div>
        <p className="text-xs text-text-secondary">{deal.description}</p>
      </Card>

      {/* ─── Parties ─────────────────────────────────── */}
      <Card className="space-y-2.5">
        <p className="text-[10px] uppercase tracking-wider text-text-muted">
          {t('admin.detail.sectionParties')}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md border border-border/40 bg-bg-tertiary/40 p-2.5">
            <p className="text-[10px] uppercase tracking-wider text-text-muted">
              {t('admin.queue.buyer')}
            </p>
            <p className="mt-1 truncate text-sm font-medium text-text-primary">
              {partyLabel(buyer)}
            </p>
          </div>
          <div className="rounded-md border border-border/40 bg-bg-tertiary/40 p-2.5">
            <p className="text-[10px] uppercase tracking-wider text-text-muted">
              {t('admin.queue.seller')}
            </p>
            <p className="mt-1 truncate text-sm font-medium text-text-primary">
              {partyLabel(seller)}
            </p>
          </div>
        </div>
        <div className="rounded-md border border-danger/30 bg-danger/5 p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-text-muted">
            {t('admin.detail.filedBy')} {partyLabel(filedBy)} ·{' '}
            {formatDateTime(dispute.createdAt)}
          </p>
          <p className="mt-1 text-xs text-text-secondary">{dispute.reason}</p>
        </div>
      </Card>

      {/* ─── Evidence ────────────────────────────────── */}
      <Card className="space-y-2.5">
        <p className="text-[10px] uppercase tracking-wider text-text-muted">
          {t('admin.detail.sectionEvidence')}
        </p>
        {evidence.length === 0 ? (
          <p className="text-xs text-text-secondary">{t('admin.detail.noEvidence')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {evidence.map((ev) => (
              <li
                key={ev.id}
                className="rounded-md border border-border/40 bg-bg-tertiary/40 p-2.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-text-muted">
                    {evidenceSourceLabel(ev, detail, t)} · {ev.type}
                  </span>
                  <span className="text-[10px] text-text-muted">
                    {formatDateTime(ev.createdAt)}
                  </span>
                </div>
                <p className="mt-1 break-words text-xs text-text-secondary">
                  {ev.content}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ─── Message thread ────────────────────────────── */}
      <DisputeChat
        disputeId={dispute.id}
        currentUserId={userSnapshot?.id ?? null}
        isResolved={isResolved}
      />

      {/* ─── Decide outcome ──────────────────────────── */}
      {!isResolved && (
        <Card className="space-y-3">
          <p className="text-[10px] uppercase tracking-wider text-text-muted">
            {t('admin.detail.sectionDecide')}
          </p>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="outcome">{t('admin.detail.sectionDecide')}</Label>
            <select
              id="outcome"
              value={form.outcome}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, outcome: e.target.value as Outcome }))
              }
              className="rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple"
            >
              <option value="buyer_wins">{t('admin.detail.outcome.buyerWins')}</option>
              <option value="seller_wins">{t('admin.detail.outcome.sellerWins')}</option>
              <option value="mutual_cancel">
                {t('admin.detail.outcome.mutualCancel')}
              </option>
              <option value="split">{t('admin.detail.outcome.split')}</option>
            </select>
          </div>

          {form.outcome === 'split' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="buyer-amt">
                  {t('admin.detail.buyerAmountLabel')}
                </Label>
                <Input
                  id="buyer-amt"
                  inputMode="decimal"
                  value={form.buyerAmount}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, buyerAmount: e.target.value }))
                  }
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="seller-amt">
                  {t('admin.detail.sellerAmountLabel')}
                </Label>
                <Input
                  id="seller-amt"
                  inputMode="decimal"
                  value={form.sellerAmount}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, sellerAmount: e.target.value }))
                  }
                />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="resolution-note">{t('admin.detail.noteLabel')}</Label>
            <textarea
              id="resolution-note"
              value={form.note}
              onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
              maxLength={500}
              rows={3}
              placeholder={t('admin.detail.notePlaceholder')}
              className={cn(
                'w-full resize-y rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
              )}
            />
          </div>

          {formError && (
            <p className="text-xs text-danger">{formError}</p>
          )}

          {submit.kind === 'error' && (
            <Card className="flex items-start gap-2 border-danger/40 bg-danger/10">
              <AlertCircle
                className="mt-0.5 h-4 w-4 shrink-0 text-danger"
                aria-hidden
              />
              <p className="text-xs text-text-secondary">{submit.message}</p>
            </Card>
          )}

          {submit.kind === 'success' && (
            <Card className="flex flex-col items-start gap-2 border-success/40 bg-success/10">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
                <p className="text-sm font-medium text-text-primary">
                  {t('admin.detail.success')}
                </p>
              </div>
              {submit.txHash && (
                <p className="break-all font-mono text-[11px] text-text-muted">
                  {submit.txHash}
                </p>
              )}
            </Card>
          )}

          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {submit.kind === 'submitting' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <ShieldCheck className="h-4 w-4" aria-hidden />
            )}
            {submit.kind === 'submitting'
              ? t('admin.detail.submitting')
              : t('admin.detail.submit')}
          </Button>
        </Card>
      )}

      {isResolved && (
        <Card className="flex items-start gap-3 border-success/40 bg-success/10">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-text-primary">
              {t('admin.detail.success')}
            </p>
            {dispute.outcome && (
              <p className="text-xs text-text-secondary">
                {t(`admin.detail.outcome.${
                  dispute.outcome === 'buyer_wins'
                    ? 'buyerWins'
                    : dispute.outcome === 'seller_wins'
                      ? 'sellerWins'
                      : dispute.outcome === 'mutual_cancel'
                        ? 'mutualCancel'
                        : 'split'
                }` as const)}
              </p>
            )}
            {dispute.resolutionNote && (
              <p className="text-xs text-text-secondary">{dispute.resolutionNote}</p>
            )}
          </div>
        </Card>
      )}

      {/* ─── Confirmation dialog (2-step) ────────────── */}
      <Dialog
        open={submit.kind === 'confirming'}
        onOpenChange={(open) => {
          if (!open && submit.kind === 'confirming') setSubmit({ kind: 'idle' });
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-danger">
              <ShieldAlert className="h-5 w-5" aria-hidden />
              {t('admin.detail.confirmTitle')}
            </DialogTitle>
            <DialogDescription>{t('admin.detail.confirmBody')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmit({ kind: 'idle' })}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleResolve()}
            >
              <ShieldCheck className="h-4 w-4" aria-hidden />
              {t('admin.detail.confirmContinue')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
